import { debounce } from "lodash";
import { Node as ProseMirrorNode } from "prosemirror-model";
import { EditorState, Plugin, PluginKey, Selection } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView } from "prosemirror-view";

import { inGoogleMeet, inMSTeams } from "~/lib/browserExtension";
import { decodeGlobalId } from "~/lib/relayUtil";
import { getInitials, isZoom, str2AttendeeColors, str2ColorHex } from "~/lib/utils";
import WebsocketClient, { GroupEmitter } from "~/lib/websocket";

import { Extension } from "../extension";
import { Collab, Rebaseable, type SelectionInfo } from "./collab";
import { posIsInCollapsedSection } from "./collapse";

export const MultiCursorPluginKey = new PluginKey<MultiCursorState>("fellow-multicursor");

interface UserInfo {
	id: string;
	email: string;
	firstName: string;
	fullName: string;
	lastName: string;
	image?: string;
	isAnonymous?: boolean;
	isExternal?: boolean;
}

interface MultiCursorConfig {
	documentId: string;
	getView: () => EditorView | null;
	sessionId: string;
	publicLinkId?: string;
	inviteId?: string;
	collab: Collab;
	user: UserInfo;
	onUserPresenceChange: (presence: UserPresence) => void;
}

export type Platform = "WEB" | "HANGOUTS_MEET" | "ZOOM" | "MS_TEAMS";

export interface UserPresence {
	sessionId: string;
	lastActive: number; // number of milliseconds since 1970/01/01
	user: UserInfo;
	selection: SelectionInfo | null;
	platform: Platform;
	isCurrentlyViewing?: boolean;
}

type websocketMessage =
	| {
			type: "add";
			userPresence: UserPresence;
	  }
	| {
			type: "delete";
			sessionId: string;
	  }
	| {
			type: "update";
			userPresence: UserPresence;
	  }
	| {
			type: "hydrate";
			users: UserPresence[];
	  }
	| { type: "ask" }
	| {
			type: "onScreen";
			sessionId: string;
	  }
	| {
			type: "offScreen";
			sessionId: string;
	  };

export class MultiCursorState {
	config: MultiCursorConfig;
	users: { [sessionId: string]: UserPresence };
	socket: GroupEmitter | null;
	decorationSet: DecorationSet;
	_disableSocket: boolean;
	timeout = 60000;
	removeTimedOutDecorationInterval?: number;
	static fromState = (state: EditorState): MultiCursorState | null | undefined => {
		return MultiCursorPluginKey.getState(state);
	};

	constructor(config: MultiCursorConfig) {
		this.socket = null;
		this.config = config;
		this.users = {};
		this.decorationSet = DecorationSet.empty;
		this._disableSocket = false;
	}

	static getValidPos = (doc: ProseMirrorNode, pos: number) => {
		const resolvedPos = doc.resolve(pos);
		const backwardSelection = Selection.findFrom(resolvedPos, -1, false);
		const forwardSelection = Selection.findFrom(resolvedPos, 1, false);
		return backwardSelection ? backwardSelection.from : forwardSelection ? forwardSelection.from : pos;
	};

	onLoad = () => {
		const [, pk] = decodeGlobalId(this.config.documentId);
		this.socket = WebsocketClient.getInstance().getOrJoinGroup(`prose_cursor.${pk}`, {
			sessionId: this.config.sessionId,
			publicLinkId: this.config.publicLinkId,
			inviteId: this.config.inviteId,
		});
		this.decorationSet = DecorationSet.create((this.view as EditorView).state.doc, []);
		this.socket.on<websocketMessage>("message", this.onSocketMessage);
		this.socket.on("open", this.onSocketOpen);
		this.removeTimedOutDecorationInterval = window.setInterval(this.removeTimedOutDecoration, this.timeout / 2);
	};

	private onSocketOpen = () => {
		(this.socket as GroupEmitter).send({
			type: "add",
			userPresence: {
				sessionId: this.config.sessionId,
				user: this.config.user,
				selection: null,
				platform: this.getUserPlatform(),
			},
		});
	};

	private onSocketMessage = async (message: websocketMessage) => {
		if (!this.socket) return;
		switch (message.type) {
			case "add":
				this.setUserPresenceValue(message.userPresence);
				this.updateDecoration(message.userPresence.sessionId);
				break;
			case "delete":
				if (this.users[message.sessionId]?.isCurrentlyViewing) this.setCurrentlyViewing(false, message.sessionId);
				delete this.users[message.sessionId];
				this.updateDecoration(message.sessionId);
				break;
			case "update":
				this.setUserPresenceValue(message.userPresence);
				await this.setSessionSelection(message.userPresence.sessionId, message.userPresence.selection);
				if (message.userPresence.sessionId != this.config.sessionId) {
					this.updateDecoration(message.userPresence.sessionId);
				}
				break;
			case "hydrate":
				message.users.forEach(item => this.setUserPresenceValue(item));
				this.updateDecoration();
				break;
			case "ask":
				this.socket.send({
					type: "add",
					userPresence: {
						sessionId: this.config.sessionId,
						user: this.config.user,
						selection: null,
						platform: this.getUserPlatform(),
					},
				});
				this.updateCursor(this.view ? this.view.state.selection : null);
				break;
			case "onScreen":
				if (this.users[message.sessionId]) this.setCurrentlyViewing(true, message.sessionId);
				break;
			case "offScreen":
				if (this.users[message.sessionId]) this.setCurrentlyViewing(false, message.sessionId);
				break;
			default:
				break;
		}
	};

	private _updateCursor = (selection: Selection | null, hasFocus = true) => {
		if (!this.socket) return;
		let selectionPayload = null;
		if (selection) {
			selectionPayload = { from: selection.from, to: selection.to, headPos: selection.head, hasFocus };
		}
		this.socket.send({
			type: "update",
			userPresence: {
				sessionId: this.config.sessionId,
				user: this.config.user,
				selection: selectionPayload,
				platform: this.getUserPlatform(),
			},
		});
	};

	rebaseRemoteSelection = (selection: SelectionInfo | null, unconfirmedSteps: Rebaseable[]): SelectionInfo | null => {
		if (!selection) return selection;
		const map = (pos: number) => unconfirmedSteps.reduce((acc, s) => s.step.getMap().map(acc), pos);
		return {
			from: map(selection.from),
			to: map(selection.to),
			headPos: map(selection.headPos),
			hasFocus: selection.hasFocus,
		};
	};

	setSessionSelection = async (sessionId: string, selection: SelectionInfo | null) => {
		if (!(sessionId in this.users)) {
			if (!this.socket) return;
			try {
				const data = await this.socket.request<UserPresence | null>(
					{ type: "get_session", sessionId: sessionId },
					3000,
					2,
				);
				if (data) {
					this.users[sessionId] = data;
					this.users[sessionId].lastActive = new Date().getTime();
				} else {
					return;
				}
			} catch {
				return;
			}
		}
		this.users[sessionId]["selection"] = selection;
		this.users[sessionId].lastActive = new Date().getTime();
	};

	updateCursor = debounce(this._updateCursor, 200);
	destroy = () => {
		this.updateCursor.flush();
		if (this.socket) {
			this.socket.off("message", this.onSocketMessage);
			this.socket.off("open", this.onSocketOpen);
			this.socket.close();
			this.socket = null;
		}
		window.clearInterval(this.removeTimedOutDecorationInterval);
	};

	createDecorationsForSession = (sessionId: string, state: EditorState) => {
		if (!this.view) return [];
		const presence = this.users[sessionId];
		if (!presence || !presence.selection) return [];
		const nowTs = new Date().getTime();
		if (nowTs - presence.lastActive > this.timeout) return [];
		const decos = [];
		const selection = this.rebaseRemoteSelection(presence.selection, this.config.collab.unconfirmedSteps);
		if (!selection) return [];
		if (selection.from !== selection.to) {
			// Ensure selection is within the document
			const from = Math.max(0, Math.min(state.doc.content.size, selection.from)),
				to = Math.max(0, Math.min(state.doc.content.size, selection.to));

			const selectedContent = state.doc.cut(from, to).content;
			const { selection: avatarSelectionColor } = str2AttendeeColors(presence.user.fullName);

			if (
				selectedContent.childCount === 1 &&
				selectedContent.firstChild &&
				["embed", "image_strip"].includes(selectedContent.firstChild.type.name)
			) {
				decos.push(
					Decoration.node(
						selection.from,
						selection.to,
						{
							class: "multicursor-select",
							style: `
								background-color: #${avatarSelectionColor};
								border-radius: 6px;
							`,
						},
						{
							// @ts-ignore
							sessionId: presence.sessionId,
						},
					),
				);
			} else if (selectedContent.childCount > 1) {
				selectedContent.descendants((node, pos) => {
					const from = pos + selection.from;
					const to = from + node.nodeSize;

					decos.push(
						Decoration.node(
							from,
							to,
							{
								// This intends to mimic the styles for multiselect, but with avatar colours
								class: "multicursor-select",
								style: `
										box-shadow: 0 0 0 2px #${avatarSelectionColor};
										background-color: #${avatarSelectionColor};
										border-radius: 1px;
									`,
							},
							{
								multiCursor: true,
								sessionId: presence.sessionId,
								key: presence.sessionId + selection.hasFocus,
							},
						),
					);
					return false;
				});
			} else {
				decos.push(
					Decoration.inline(
						selection.from,
						selection.to,
						{
							class: "multicursor-select",
							style: `background-color: #${avatarSelectionColor}`,
						},
						{
							// @ts-ignore
							sessionId: presence.sessionId,
						},
					),
				);
			}
		}
		let validPos;
		try {
			validPos = MultiCursorState.getValidPos(state.doc, selection.headPos);
		} catch (error) {
			return decos;
		}
		if (posIsInCollapsedSection(state, validPos)) return decos;
		const resolvedPos = state.doc.resolve(validPos);
		decos.push(
			Decoration.widget(
				validPos,
				(_view, _getPos) => {
					const el = document.createElement("span");
					try {
						el.classList.add("cursor");
						if (presence.selection && !presence.selection.hasFocus) {
							el.classList.add("cursor--inactive");
						}
						const color = str2ColorHex(presence.user.fullName);
						el.style.boxShadow = `0px 0px 0px 1px #${color}`;
						const tag = document.createElement("span");
						tag.classList.add("cursor-flag");
						tag.style.backgroundColor = `#${color}`;
						tag.setAttribute("data-initials", getInitials(presence.user.fullName, { maxLength: 2 }));
						tag.setAttribute("data-fullname", presence.user.fullName);
						el.appendChild(tag);
						return el;
					} catch (error) {
						console.error(error);
					}
					el.style.display = "none";
					return el;
				},
				{
					sessionId: presence.sessionId,
					key: presence.sessionId + presence.selection.hasFocus,
					marks: Array.from(resolvedPos.marks()),
					side: resolvedPos.node().textContent ? 1 : -1,
				},
			),
		);
		return decos;
	};

	_renderDecorations = () => {
		if (!this.view) return;
		this.view.dispatch(this.view.state.tr.setMeta("renderCursors", true));
	};
	renderDecorations = debounce(this._renderDecorations, 100);

	removeTimedOutDecoration = () => {
		const nowTs = new Date().getTime();
		const decosToRemove = this.decorationSet.find(
			undefined,
			undefined,
			d =>
				!this.users[d.sessionId] ||
				(nowTs - this.users[d.sessionId].lastActive > this.timeout &&
					(!this.users[d.sessionId].selection?.hasFocus || !this.users[d.sessionId].isCurrentlyViewing)),
		);
		this.decorationSet = this.decorationSet.remove(decosToRemove);
		this.renderDecorations();
	};

	updateDecoration = (sessionId: string | null = null, state: EditorState | null = null) => {
		const editorState = (() => {
			if (state) return state;
			return this.view && this.view.state;
		})();
		if (!editorState) return;
		if (sessionId === this.config.sessionId) return;
		if (!sessionId) {
			const decos = Object.keys(this.users).reduce((acc, sId) => {
				if (sId === this.config.sessionId) return acc;
				const ds = this.createDecorationsForSession(sId, editorState);
				return [...acc, ...ds];
			}, [] as Decoration[]);
			this.decorationSet = DecorationSet.create(editorState.doc, decos);
			this.renderDecorations();
			return;
		}
		const decosToRemove = this.decorationSet.find(
			undefined,
			undefined,
			d => !(d.sessionId in this.users) || !this.users[d.sessionId].selection,
		);
		this.decorationSet = this.decorationSet.remove(decosToRemove);
		const decoToUpdate = this.decorationSet.find(undefined, undefined, d => d.sessionId === sessionId);
		if (decoToUpdate) {
			this.decorationSet = this.decorationSet.remove(decoToUpdate);
		}
		const newDecos = this.createDecorationsForSession(sessionId, editorState);
		this.decorationSet = this.decorationSet.add(editorState.doc, newDecos);
		this.removeTimedOutDecoration();
		this.renderDecorations();
	};

	onBlur = (_e: React.FocusEvent<HTMLDivElement>) => {
		if (!this.view) return;
		if (!this.view.hasFocus()) {
			this.updateCursor(null);
		} else {
			this.updateCursor(this.view.state.selection, false);
		}
	};

	onFocus = () => {
		if (!this.view) return;
		if (!this.view.hasFocus()) {
			this.updateCursor(null);
		} else {
			this.updateCursor(this.view.state.selection);
		}
	};

	setUserPresenceValue = (presence: UserPresence) => {
		const { sessionId, isCurrentlyViewing } = presence;
		const viewingChanged = this.users[sessionId]?.isCurrentlyViewing !== isCurrentlyViewing;

		this.users[sessionId] = presence;
		this.users[sessionId].lastActive = Math.floor(new Date().getTime());
		this.updateDecoration(sessionId);

		if (isCurrentlyViewing && viewingChanged) this.setCurrentlyViewing(true, sessionId);
	};

	setCurrentlyViewing = (value: boolean, sessionId: string) => {
		this.users[sessionId].isCurrentlyViewing = value;

		if (!value) this.users[sessionId].lastActive = Math.floor(new Date().getTime());

		this.config.onUserPresenceChange(this.users[sessionId]);
	};

	onScreenVisible = () => {
		if (!this.view || !this.socket) return;

		this.socket.send({
			type: "onScreen",
			sessionId: this.config.sessionId,
		});
	};

	onScreenHidden = () => {
		if (!this.view || !this.socket || !this.users[this.config.sessionId]?.isCurrentlyViewing) return;

		this.socket.send({
			type: "offScreen",
			sessionId: this.config.sessionId,
		});
	};
	get view() {
		return this.config.getView();
	}

	getUserPlatform = (): Platform => {
		if (isZoom) return "ZOOM";
		if (inGoogleMeet) return "HANGOUTS_MEET";
		if (inMSTeams) return "MS_TEAMS";
		return "WEB";
	};
}

export class MultiCursor extends Extension {
	get name() {
		return "multicursor";
	}
	get plugins() {
		const extension = this;
		return [
			new Plugin<MultiCursorState>({
				key: MultiCursorPluginKey,
				props: {
					decorations: (state: EditorState) => {
						return MultiCursorState.fromState(state)?.decorationSet;
					},
				},
				appendTransaction: (transactions, oldState, newState) => {
					const tr = transactions.find(tr => tr.getMeta("updateSessionCursor"));
					if (!tr) return null;
					const update = tr.getMeta("updateSessionCursor");
					const state = MultiCursorPluginKey.getState(newState) as MultiCursorState;
					if (update && state) {
						if (update.sessionId) state.setSessionSelection(update.sessionId, update.selection);
						state.updateDecoration(update.sessionId, newState);
						state.renderDecorations.cancel();
					}
					return newState.tr.setMeta("renderRemoteCursor", true);
				},
				state: {
					init: (_config, state: EditorState) => {
						const oldState = MultiCursorState.fromState(state);
						if (oldState) {
							return oldState;
						}
						return new MultiCursorState({
							...(this.options as MultiCursorConfig),
							getView: () => extension.editorView || null,
						});
					},
					apply: (tr, value, oldState, newState) => {
						if (tr.docChanged) {
							// This does not work when selecting at the end of a block
							value.decorationSet = value.decorationSet.map(tr.mapping, newState.doc);
						}
						if (!tr.docChanged && tr.selectionSet) {
							value.updateCursor(newState.selection);
						}
						return value;
					},
				},
			}),
		];
	}
	destroy() {
		if (!this.editorView) return;
		const state = MultiCursorState.fromState(this.editorView.state);
		state?.destroy();
	}
	onEditorBlur(e: React.FocusEvent<HTMLDivElement>) {
		if (!this.editorView) return;
		const state = MultiCursorState.fromState(this.editorView.state);
		if (state) {
			state.onBlur(e);
		}
	}
	onEditorFocus() {
		if (!this.editorView) return;
		const state = MultiCursorState.fromState(this.editorView.state);
		if (state) {
			state.onFocus();
		}
	}
	onScreenVisible() {
		if (!this.editorView) return;
		const state = MultiCursorState.fromState(this.editorView.state);
		if (state) state.onScreenVisible();
	}
	onScreenHidden() {
		if (!this.editorView) return;
		const state = MultiCursorState.fromState(this.editorView.state);
		if (state) state.onScreenHidden();
	}
	init() {
		if (!this.editorView) return;
		const state = MultiCursorState.fromState(this.editorView.state);
		if (state) {
			state.onLoad();
		}
	}
}

export default MultiCursor;
